<template>
  <div class="rs-form-strength-input">
    <label v-if="label">
      {{ label }}
    </label>
    <RsFormInput
      :value="boundValue"
      type="text"
      :debounce="100"
      class="flex-fill mb-0"
      @input="onInput"
    />
    <div class="strength-bar-wrapper">
      <div class="strength-bar">
        <div
          class="strength-bar__overlay"
          :class="valueStrength.strengthColor"
          :style="{ width: valueStrength.strength + '%' }"
        />
      </div>
      <div class="strength-bar-counter">
        <p>{{ (boundValue || '').length }}/{{ tooMuchStrengthLength }}</p>
      </div>
    </div>
    <div class="strength-bar-info">
      <div id="tooltip-seo-title">
        <RsIcon
          icon="info-circle"
          weight="fas"
        />
      </div>
      <p>{{ valueStrength.strengthText }}</p>
    </div>
  </div>
</template>

<script lang="ts" scoped>
import { defineComponent } from 'vue';

type TValueStrength = {
  strength: number,
  strengthColor: string,
  strengthText: string,
};

/**
 * Shows a strength bar below input that describes optimal string value length
 */
export default defineComponent({
  name: 'RsFormStrengthInput',

  props: {
    /**
     * v-model value prop
     */
    value: {
      type: String,
      default: '',
    },

    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: String,
      default: undefined,
    },

    /**
     * Value prefix (unused at the moment), may need implementing later
     */
    prefix: {
      type: String,
      default: '',
    },

    /**
     * Label for the form control
     */
    label: {
      type: String,
      default: '',
    },

    /**
     * What's considered too short of a value
     */
    tooLowStrengthLength: {
      type: Number,
      default: 54, // 23 for seo title
    },

    /**
     * What's considered average value length
     * Between this and tooMuchStrengthLength is optimal
     */
    avgStrengthLength: {
      type: Number,
      default: 107, // 46 for seo title
    },

    /**
     * What's considered too much value length
     */
    tooMuchStrengthLength: {
      type: Number,
      default: 155, // 60 for seo title
    },
  },

  emits: ['input', 'update:modelValue'],

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    valueStrength(): TValueStrength {
      return this.getStrength(this.boundValue, this.tooLowStrengthLength, this.avgStrengthLength, this.tooMuchStrengthLength);
    },
  },

  watch: {
    '$rsGlobals.$language': function () {
      this.$forceUpdate();
    },
  },

  methods: {
    onInput(val: string): void {
      this.$emit('input', val);
      this.$emit('update:modelValue', val);
    },
    getStrength(
      input: string = '',
      avgLength: number,
      optLength: number,
      maxLength: number,
    ): TValueStrength {
      const { length } = input;

      const returnVal = {
        strength: (length / maxLength) * 100,
        strengthColor: 'error',
        strengthText: this.$ts('seoCard.strengthTooShort') as string,
      };

      if (length >= avgLength) {
        returnVal.strengthColor = 'warning';
        returnVal.strengthText = this.$ts('seoCard.strengthAverage') as string;
      }

      if (length >= optLength) {
        returnVal.strengthColor = 'success';
        returnVal.strengthText = this.$ts('seoCard.strengthOptimal') as string;
      }

      if (length > maxLength) {
        returnVal.strengthColor = 'error';
        returnVal.strengthText = this.$ts('seoCard.strengthTooLong') as string;
      }

      return returnVal;
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
.rs-form-strength-input {
  margin-bottom: 1rem;

  p {
    display: inline-block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
  }
  .strength-bar-wrapper {
    margin-top: 0.25rem;
    display: flex;
    align-items: center;
    p {
      margin: 0;
    }

    .strength-bar,
    .strength-bar__overlay {
      width: 100%;
      height: 8px;
      border-radius: 15px;
    }
    .strength-bar {
      position: relative;
      overflow: hidden;
      border-radius: 15px;
      background-color: #eee;
      &:before,
      &:after {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: $white;
        opacity: 0.4;
      }
      &:before {
        left: 33%;
      }
      &:after {
        left: 66%;
      }
      &__overlay {
        transition: width 500ms;
        &.success {
          background-color: $primary-color;
        }
        &.error {
          background-color: $error;
        }
        &.warning {
          background-color: $warning;
        }
      }
    }
    .strength-bar-counter {
      margin-left: 1rem;
      min-width: 55px;
      font-size: 0.75rem;
      text-align: right;
    }
  }

  .strength-bar-info {
    display: flex;
    align-items: center;
    .rs-icon {
      margin-right: 0.25rem;
      color: $tertiary-gray;
    }
    p {
      margin: 0;
      color: $tertiary-muted;
      font-size: 0.875rem;
    }
  }
}
</style>
